import React, { useState, useCallback, useEffect } from 'react';
import { useNavigateWithNewTab } from '../hooks/useNavigateWithNewTab';
import { handledExportError, usePdfExport } from '../hooks/usePdfExport';
import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  ExternalLink,
  AlertTriangle,
  ClipboardList,
  Download,
  ChevronDown,
} from 'lucide-react';
import type {
  NoaResponse,
  NoaUpdate,
  NoaValidationError,
} from '../services/api/types.gen';
import {
  updateNoaNoasNoaIdPatch,
  validateNoaNoasNoaIdValidatePost,
  submitNoa837iNoasNoaIdSubmitPost,
  sendNoaNoasNoaIdSendPost,
  markNotApplicableNoasNoaIdMarkNotApplicablePost,
  revertToNotReadyNoasNoaIdRevertToNotReadyPost,
  getNoa837iNoasNoaId837iGet,
  getNoaUb04PdfNoasNoaIdUb04Get,
  getPatientGeographicValueCodesApiBillingPatientPatientIdGeographicValueCodesGet,
} from '../services/api/sdk.gen';
import { PermissionSlug } from '../constants/featureFlags';
import { EntityWorkLogDialog } from './worklog/EntityWorkLogDialog';
import {
  NOA_WORK_LOG_FIELD_LABELS,
  loadNoaWorkLog,
} from './worklog/noaWorkLog';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { formatDateToMMDDYYYY, parseISOToLocalDate } from '../utils/dateUtils';
import { formatDollarAmount } from '../utils/currencyUtils';
import { downloadBlobAsFile } from '../utils/downloadFile';
import { DateField } from './FormFields';
import { PayerReasonList } from './billing/claimReason/PayerReasonList';
import { usePayerResponse } from './billing/claimReason/usePayerResponse';
import { ConditionCodesField } from './billing/worksheet/ConditionCodesField';
import { EditSection } from './billing/worksheet/EditSection';
import { OccurrenceCodesField } from './billing/worksheet/OccurrenceCodesField';
import { OccurrenceSpansField } from './billing/worksheet/OccurrenceSpansField';
import { ValueCodesField } from './billing/worksheet/ValueCodesField';
import {
  MAX_UB04_OCCURRENCE_CODES,
  MAX_UB04_REMARKS_LENGTH,
} from './billing/worksheet/types';
import type {
  OccurrenceRow,
  OccurrenceSpanRow,
  ValueRow,
} from './billing/worksheet/types';
import { NpiInput } from './NpiInput';
import { sanitizeNpiInput, validateNpi } from '../utils/npiValidation';
import {
  getUserFacingApiMessage,
  getUserFacingApiMessageAsync,
  isTestPatientAckRequired,
} from '../utils/apiErrorDetail';
import { useToast } from '../hooks/useToastContext';
import { TestPatientSubmitConfirmDialog } from './TestPatientSubmitConfirmDialog';

interface NOAFormProps {
  noa: NoaResponse;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: NoaResponse) => void;
}

// EMR-1476: mirrors the backend NOA_EDIT_LOCKED_STATUSES — once the NOA is
// with the payer (sent/acknowledged), accepted, or paid, it is read-only and
// PATCH rejects with a 409. Rejected stays editable for fix-and-resend
// (EMR-1471); not_applicable stays editable so it can be reverted.
const EDIT_LOCKED_STATUSES = new Set([
  'sent',
  'acknowledged',
  'accepted',
  'paid',
]);

function isRadixOverlayTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    (target.closest('[role="menu"]') != null ||
      target.closest('[data-radix-popper-content-wrapper]') != null)
  );
}

const ReadonlyField: React.FC<{
  label: string;
  value: string | null | undefined;
  href?: string;
}> = ({ label, value, href }) => {
  const navigateTo = useNavigateWithNewTab();
  return (
    <div className='flex flex-col gap-0.5'>
      <span className='text-xs text-muted-foreground'>{label}</span>
      <div className='flex items-center gap-1'>
        <span className='text-sm border-b border-muted text-foreground'>
          {value || '—'}
        </span>
        {href && (
          <button
            onClick={navigateTo(href)}
            className='text-muted-foreground hover:text-primary'
          >
            <ExternalLink className='h-3 w-3' />
          </button>
        )}
      </div>
    </div>
  );
};

const ErrorsPanel: React.FC<{ errors: NoaValidationError[] }> = ({
  errors,
}) => {
  if (errors.length === 0) {
    return (
      <div className='rounded-md bg-green-50 border border-green-200 p-3'>
        <p className='text-sm text-green-700 font-medium'>
          No validation errors
        </p>
        <p className='text-xs text-green-600'>This NOA is ready to send.</p>
      </div>
    );
  }
  return (
    <div className='rounded-md bg-red-50 border border-red-200 p-3 space-y-1'>
      <p className='text-sm font-medium text-red-700 flex items-center gap-1'>
        <AlertTriangle className='h-4 w-4' />
        Validation Errors
      </p>
      {errors.map((e, i) => (
        <p key={i} className='text-xs text-red-600'>
          • {e.message}
        </p>
      ))}
    </div>
  );
};

/**
 * What the payer objected to, at the top of the popup (EMR-1472). Reuses the
 * shared hook and list, so this panel and the Rejected status pill in the NOA
 * Manager can never disagree about the reasons.
 */
const RejectionReasonsPanel: React.FC<{ noaId: string }> = ({ noaId }) => {
  // Mounted only for a rejected NOA (see the render site), so the fetch is
  // always wanted here.
  const { summary, isLoading, errorMessage } = usePayerResponse(
    'noa',
    noaId,
    true
  );
  return (
    <div className='rounded-md bg-red-50 border border-red-200 p-3 space-y-2'>
      <p className='text-sm font-medium text-red-700 flex items-center gap-1'>
        <AlertTriangle className='h-4 w-4' />
        Rejection Reasons
      </p>
      {isLoading ? (
        <p className='text-xs text-muted-foreground'>Loading</p>
      ) : null}
      {!isLoading && errorMessage ? (
        <p className='text-xs text-red-700'>{errorMessage}</p>
      ) : null}
      {!isLoading && !errorMessage && summary ? (
        <PayerReasonList
          reasons={summary.reasons}
          payerClaimNumber={summary.payer_claim_control_number}
          kind='noa'
        />
      ) : null}
    </div>
  );
};

const NOAForm: React.FC<NOAFormProps> = ({ noa, open, onClose, onSaved }) => {
  const navigateTo = useNavigateWithNewTab();
  const { exportPdf, exportingId } = usePdfExport();
  const [menuPortalContainer, setMenuPortalContainer] =
    useState<HTMLElement | null>(null);
  const dialogContentRef = useCallback((node: HTMLDivElement | null) => {
    setMenuPortalContainer(node);
  }, []);
  const { user } = useStore(useShallow(state => ({ user: state.user })));
  const hasPermission = useStore(state => state.hasPermission);
  const canViewWorkLog = hasPermission(PermissionSlug.WORKLOG_VIEW);
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const loadWorkLog = useCallback(() => loadNoaWorkLog(noa.id), [noa.id]);
  const { success: showSuccess, error: showError } = useToast();
  const isAdmin = user?.role === 'admin';
  // EMR-779: these grids belong to the NOA, so they seed from the NOA response
  // rather than from the first episode's worksheet. Saving mirrors them onto that
  // worksheet server-side so they still reach the claim.
  const [conditionCodes, setConditionCodes] = useState<string[]>(
    noa.condition_codes ?? []
  );
  const [occurrenceCodes, setOccurrenceCodes] = useState<OccurrenceRow[]>(
    noa.occurrence_codes ?? []
  );
  const [occurrenceSpans, setOccurrenceSpans] = useState<OccurrenceSpanRow[]>(
    noa.occurrence_spans ?? []
  );
  const [valueCodes, setValueCodes] = useState<ValueRow[]>(
    noa.value_codes ?? []
  );
  const [remarks, setRemarks] = useState(noa.remarks ?? '');
  const [physicianNpi, setPhysicianNpi] = useState(noa.physician_npi || '');
  const [physicianFirstName, setPhysicianFirstName] = useState(
    noa.physician_first_name || ''
  );
  const [physicianLastName, setPhysicianLastName] = useState(
    noa.physician_last_name || ''
  );
  const [notes, setNotes] = useState(noa.notes || '');
  const [dcnIcnNumber, setDcnIcnNumber] = useState(noa.dcn_icn_number || '');
  const [claimNumber, setClaimNumber] = useState(noa.claim_number || '');
  const [sentDate, setSentDate] = useState(noa.sent_date || '');
  const [kxModifier, setKxModifier] = useState<boolean | null>(
    noa.kx_modifier ?? null
  );
  const [kxModifierRemarks, setKxModifierRemarks] = useState(
    noa.kx_modifier_remarks || ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingValueCodes, setIsGeneratingValueCodes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testPatientAckOpen, setTestPatientAckOpen] = useState(false);

  const validateCodeGrids = (): string | null => {
    const partialOccurrence = occurrenceCodes.some(
      r => Boolean(r.code) !== Boolean(r.date)
    );
    const partialValue = valueCodes.some(
      r => Boolean(r.code) !== Boolean(r.amount)
    );
    // A value code may appear only once per claim — the backend rejects
    // duplicates too, but catching it here gives a clearer message.
    const seenValueCodes = new Set<string>();
    const duplicateValueCodes = new Set<string>();
    for (const r of valueCodes) {
      if (!r.code) continue;
      if (seenValueCodes.has(r.code)) duplicateValueCodes.add(r.code);
      seenValueCodes.add(r.code);
    }
    if (duplicateValueCodes.size > 0) {
      const codes = [...duplicateValueCodes].sort().join(', ');
      return `Each value code can only be entered once. Remove the duplicate row${
        duplicateValueCodes.size > 1 ? 's' : ''
      } for value code${duplicateValueCodes.size > 1 ? 's' : ''} ${codes}.`;
    }
    const partialSpan = occurrenceSpans.some(
      r =>
        [r.code, r.from_date, r.through_date].some(Boolean) &&
        ![r.code, r.from_date, r.through_date].every(Boolean)
    );
    if (partialOccurrence || partialValue || partialSpan) {
      return 'Complete or remove code rows missing a date or amount.';
    }
    if (
      occurrenceSpans.some(
        r => r.from_date && r.through_date && r.through_date < r.from_date
      )
    ) {
      return 'Occurrence span end date must be on or after the start date.';
    }
    return null;
  };

  const remarksLength = remarks.length;
  const remarksTooLong = remarksLength > MAX_UB04_REMARKS_LENGTH;
  const kxModifierRemarksLength = kxModifierRemarks.length;
  const kxModifierRemarksTooLong =
    kxModifierRemarksLength > MAX_UB04_REMARKS_LENGTH;

  const [localValidationErrors, setLocalValidationErrors] = useState<
    NoaValidationError[]
  >(noa.validation_errors);
  const [localStatus, setLocalStatus] = useState(noa.status);

  useEffect(() => {
    setLocalStatus(noa.status);
    setLocalValidationErrors(noa.validation_errors);
  }, [noa.id, noa.status, noa.validation_errors]);

  const handleExport = async (format: 'edi' | 'json') => {
    const resp = await getNoa837iNoasNoaId837iGet({
      path: { noa_id: noa.id },
      query: { format },
      responseType: 'blob',
    });
    if (resp.error) {
      setError(
        await getUserFacingApiMessageAsync(resp, 'Failed to download 837I file')
      );
      return;
    }
    if (!resp.data) return;
    downloadBlobAsFile(resp.data as BlobPart | Blob, `noa_${noa.id}.${format}`);
  };

  const handleExportPdf = () => {
    void exportPdf(
      noa.id,
      async () => {
        const resp = await getNoaUb04PdfNoasNoaIdUb04Get({
          path: { noa_id: noa.id },
          // UB-04 export returns application/pdf, so this caller intentionally
          // treats the generated response as a Blob.
          responseType: 'blob',
        });
        if (resp.error) {
          const message = await getUserFacingApiMessageAsync(
            resp,
            'Failed to download UB-04 PDF'
          );
          setError(message);
          throw handledExportError(message);
        }
        if (!resp.data) return null;
        const blob =
          resp.data instanceof Blob
            ? resp.data
            : new Blob([resp.data as BlobPart]);
        return { blob };
      },
      'UB-04 PDF'
    );
  };

  // Server truth first (`editable`), local status set as belt-and-braces so a
  // just-transitioned status (e.g. after Force Mark Sent) locks immediately.
  const readOnly =
    noa.editable === false || EDIT_LOCKED_STATUSES.has(localStatus);

  // A NOA that hasn't been sent yet but still has validation errors would export
  // a claim with known-bad data, and the backend rejects the submit with a 409.
  // Gate the pre-submission actions; already-sent NOAs stay exportable so a
  // biller can still pull the paperwork for what went out.
  const blockedByErrors =
    localStatus === 'ready_to_send' && localValidationErrors.length > 0;

  // Mirrors the backend NOA_EXPORTABLE_STATUSES gate: every status except
  // not_ready and not_applicable, so the paperwork stays retrievable after
  // the payer accepts/rejects the NOA or it is marked paid.
  const canExport =
    !blockedByErrors &&
    localStatus !== 'not_ready' &&
    localStatus !== 'not_applicable';

  const handleForceMarkSent = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const resp = await sendNoaNoasNoaIdSendPost({
        path: { noa_id: noa.id },
      });
      if (resp.error)
        throw new Error(
          getUserFacingApiMessage(resp, 'Failed to mark NOA as sent')
        );
      const updated = resp.data;
      setLocalStatus(updated.status);
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkNotApplicable = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const resp = await markNotApplicableNoasNoaIdMarkNotApplicablePost({
        path: { noa_id: noa.id },
      });
      if (resp.error)
        throw new Error(
          getUserFacingApiMessage(resp, 'Failed to mark as not applicable')
        );
      const updated = resp.data;
      setLocalStatus(updated.status);
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertToNotReady = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const resp = await revertToNotReadyNoasNoaIdRevertToNotReadyPost({
        path: { noa_id: noa.id },
      });
      if (resp.error)
        throw new Error(
          getUserFacingApiMessage(resp, 'Failed to revert status')
        );
      const updated = resp.data;
      setLocalStatus(updated.status);
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  // Fetches the CBSA (VC 61) and FIPS county (VC 85) derived from the
  // patient's ZIP and loads them into the value-codes grid. Nothing is
  // persisted until the user saves.
  const handleGenerateValueCodes = async () => {
    setIsGeneratingValueCodes(true);
    setError(null);
    try {
      const resp =
        await getPatientGeographicValueCodesApiBillingPatientPatientIdGeographicValueCodesGet(
          {
            path: { patient_id: noa.patient_id },
          }
        );
      if (resp.error || !resp.data) {
        throw new Error(
          getUserFacingApiMessage(resp, 'Failed to generate value codes')
        );
      }
      // Geographic value-code amounts are whole-number codes (CBSA/FIPS); the
      // claim expects them as dollar-style amounts, e.g. "31084.00".
      const generated: ValueRow[] = [];
      const missing: string[] = [];
      if (resp.data.value_code_61) {
        generated.push({
          code: '61',
          amount: formatDollarAmount(resp.data.value_code_61),
        });
      } else {
        missing.push('61');
      }
      if (resp.data.value_code_85) {
        generated.push({
          code: '85',
          amount: formatDollarAmount(resp.data.value_code_85),
        });
      } else {
        missing.push('85');
      }
      if (generated.length === 0) {
        throw new Error(
          "No value codes could be generated. Check the patient's ZIP code."
        );
      }
      // Update existing 61/85 rows in place so the user's ordering is
      // preserved; only append codes that aren't in the grid yet. A code the
      // lookup did NOT return is left untouched — an existing row may hold a
      // value the biller entered, and losing it to a lookup failure on our end
      // would be worse than keeping it (the toast below calls it out instead).
      setValueCodes(prev => {
        const next = prev.filter(r => r.code || r.amount);
        for (const gen of generated) {
          const i = next.findIndex(r => r.code === gen.code);
          if (i >= 0) {
            next[i] = { ...next[i], amount: gen.amount };
          } else {
            next.push(gen);
          }
        }
        return next;
      });
      if (missing.length > 0) {
        showError(
          'Value Code Not Generated',
          `Value code ${generated.map(g => g.code).join(' and ')} was loaded, but value code ${missing.join(
            ' and '
          )} could not be generated from the patient's ZIP code. Any existing value was left unchanged.`
        );
      } else {
        showSuccess(
          'Value Codes Generated',
          'Value codes 61 and 85 were loaded. Save to keep them.'
        );
      }
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to generate value codes';
      setError(message);
      showError('Generate Failed', message);
    } finally {
      setIsGeneratingValueCodes(false);
    }
  };

  const patientDob = noa.patient_dob
    ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.patient_dob))
    : null;
  const socDate = noa.soc_date
    ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.soc_date))
    : null;

  const buildPayload = (): NoaUpdate => ({
    physician_npi: sanitizeNpiInput(physicianNpi) || null,
    physician_first_name: physicianFirstName.trim() || null,
    physician_last_name: physicianLastName.trim() || null,
    notes: notes.trim() || null,
    dcn_icn_number: dcnIcnNumber || null,
    claim_number: claimNumber || null,
    sent_date: sentDate || null,
    kx_modifier: kxModifier,
    kx_modifier_remarks: kxModifierRemarks || null,
    // Incomplete rows are dropped rather than sent: the backend rejects a code
    // without its date/amount, and validateCodeGrids already blocks a save that
    // has one half-filled.
    condition_codes: conditionCodes.map(c => c.trim()).filter(Boolean),
    occurrence_codes: occurrenceCodes.filter(r => r.code && r.date),
    occurrence_spans: occurrenceSpans.filter(
      r => r.code && r.from_date && r.through_date
    ),
    value_codes: valueCodes.filter(r => r.code && r.amount),
    remarks: remarks.trim() || null,
  });

  const doSave = async (): Promise<NoaResponse | null> => {
    const npiSanitized = sanitizeNpiInput(physicianNpi);
    const npiErr = validateNpi(npiSanitized);
    if (npiErr) {
      throw new Error(npiErr);
    }
    const codeGridError = validateCodeGrids();
    if (codeGridError) {
      throw new Error(codeGridError);
    }
    if (remarks.length > MAX_UB04_REMARKS_LENGTH) {
      throw new Error(
        `FL 80 remarks supports ${MAX_UB04_REMARKS_LENGTH} characters. Shorten remarks.`
      );
    }
    if (kxModifierRemarks.length > MAX_UB04_REMARKS_LENGTH) {
      throw new Error(
        `NOA remarks supports ${MAX_UB04_REMARKS_LENGTH} characters. Shorten remarks.`
      );
    }
    const payload = buildPayload();
    const patchResp = await updateNoaNoasNoaIdPatch({
      path: { noa_id: noa.id },
      body: payload,
    });
    if (patchResp.error) {
      throw new Error(getUserFacingApiMessage(patchResp, 'Failed to save NOA'));
    }
    const validateResp = await validateNoaNoasNoaIdValidatePost({
      path: { noa_id: noa.id },
    });
    if (validateResp.error) {
      throw new Error(
        getUserFacingApiMessage(validateResp, 'Failed to validate NOA')
      );
    }
    return validateResp.data;
  };

  const handleSave = async (closeAfter: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await doSave();
      if (updated) {
        setLocalValidationErrors(updated.validation_errors);
        setLocalStatus(updated.status);
        onSaved(updated);
        showSuccess('NOA Saved', 'Notice of Admission was updated.');
        if (closeAfter) onClose();
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'An error occurred';
      setError(message);
      showError('Save Failed', message);
    } finally {
      setIsSaving(false);
    }
  };

  // The submit step, separated so the test-patient confirmation can re-run it
  // with the acknowledgement. Manages its own saving state.
  const sendNoa = async (acknowledge: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      const sendResp = await submitNoa837iNoasNoaIdSubmitPost({
        path: { noa_id: noa.id },
        query: acknowledge ? { acknowledge_test_patient: true } : undefined,
      });
      if (!acknowledge && isTestPatientAckRequired(sendResp)) {
        setTestPatientAckOpen(true);
        return;
      }
      if (sendResp.error) {
        throw new Error(
          getUserFacingApiMessage(sendResp, 'Failed to submit NOA to Medicare')
        );
      }
      onSaved(sendResp.data.noa);
      onClose();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'An error occurred';
      setError(message);
      // Toast too — the 409 detail says what's missing (e.g. clinic billing
      // contact) and who can fix it, and the dialog may already be closing.
      showError('Send Failed', message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    setIsSaving(true);
    setError(null);
    let updated;
    try {
      updated = await doSave();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
      setIsSaving(false);
      return;
    }
    if (!updated) {
      setIsSaving(false);
      return;
    }
    setLocalValidationErrors(updated.validation_errors);
    setLocalStatus(updated.status);
    // A rejected NOA keeps its status through validation (EMR-1471), so the
    // errors, not the status, say whether it can go out again.
    const sendable =
      updated.status === 'ready_to_send' ||
      (updated.status === 'rejected' && updated.validation_errors.length === 0);
    if (!sendable) {
      // The billing contact is admin-only (Clinic Management), so surface that
      // error verbatim — telling the biller to "fix it" would be a dead end.
      const adminError = updated.validation_errors.find(
        e => e.field === 'billing_contact'
      );
      const message =
        adminError?.message ??
        'NOA has validation errors and cannot be sent. Please fix them first.';
      setError(message);
      showError('Cannot Send NOA', message);
      onSaved(updated);
      setIsSaving(false);
      return;
    }
    await sendNoa(false);
  };

  // Block saves and submissions while the value-code lookup is in flight —
  // otherwise a save captures the pre-generation grid (and may close the form)
  // before the generated codes land.
  const isBusy = isSaving || isGeneratingValueCodes;

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        ref={dialogContentRef}
        className='max-w-5xl h-[85vh] max-h-[85vh] overflow-hidden flex flex-col gap-0 p-0'
        onInteractOutside={e => {
          if (isRadixOverlayTarget(e.target)) {
            e.preventDefault();
          }
        }}
        onFocusOutside={e => {
          if (isRadixOverlayTarget(e.target)) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className='shrink-0 border-b px-6 py-4 pr-12'>
          <DialogTitle>
            Notice of Admission — {noa.patient_first_name}{' '}
            {noa.patient_last_name}
          </DialogTitle>
        </DialogHeader>

        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
          {readOnly ? (
            <div className='mb-4 rounded-md border bg-muted p-3'>
              <p className='text-sm text-muted-foreground'>
                Read-only — this NOA has been{' '}
                {localStatus === 'paid'
                  ? 'paid'
                  : localStatus === 'accepted'
                    ? 'accepted by the payer'
                    : 'sent to the payer'}{' '}
                and can no longer be edited.
                {noa.accepted_externally
                  ? ' Recorded as accepted from the external EMR; Roger did not send this notice and will not send another.'
                  : ''}{' '}
                Downloads are still available.
              </p>
            </div>
          ) : null}

          {/* Payer rejection reasons — only for a rejected NOA, so a
              non-rejected NOA sends no payer-response request. */}
          {localStatus === 'rejected' ? (
            <div className='mb-4'>
              <RejectionReasonsPanel noaId={noa.id} />
            </div>
          ) : null}

          {/* Validation errors — full width, below header */}
          <div>
            <ErrorsPanel errors={localValidationErrors} />
          </div>

          <div className='mt-4 space-y-4'>
            {/* Patient / Admission (readonly) */}
            <div className='rounded-md border p-3 space-y-3'>
              <div className='flex items-center gap-2'>
                <p className='text-xs font-semibold uppercase text-muted-foreground'>
                  Patient &amp; Admission Info
                </p>
                <button
                  onClick={navigateTo(noa.patient_tab_url)}
                  className='text-muted-foreground hover:text-primary'
                >
                  <ExternalLink className='h-3 w-3' />
                </button>
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <ReadonlyField
                  label='Patient Name'
                  value={`${noa.patient_first_name} ${noa.patient_last_name}`}
                />
                <ReadonlyField label='MRN' value={noa.patient_mrn} />
                <ReadonlyField label='Date of Birth' value={patientDob} />
                <ReadonlyField
                  label='Subscriber ID'
                  value={noa.subscriber_id}
                />
                <ReadonlyField
                  label='Address'
                  value={
                    [
                      noa.patient_address,
                      noa.patient_city,
                      noa.patient_state,
                      noa.patient_zip,
                    ]
                      .filter(Boolean)
                      .join(', ') || null
                  }
                />
                <ReadonlyField label='Start of Care' value={socDate} />
                <ReadonlyField
                  label='Diagnosis Code'
                  value={
                    noa.admitting_diagnosis_omitted
                      ? `${noa.submitted_dx_code} — admitting diagnosis not sent (insurance configuration)`
                      : `${noa.submitted_dx_code} — standard code used on every NOA`
                  }
                />
                <ReadonlyField label='Payer' value={noa.payer_name} />
                <ReadonlyField label='Agency' value={noa.agency_name} />
                <ReadonlyField label='Branch' value={noa.branch_name} />
                <ReadonlyField label='Type of Bill' value='32A' />
                <ReadonlyField
                  label='Status'
                  value={localStatus
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, c => c.toUpperCase())}
                />
              </div>
            </div>

            {/* Editable fields */}
            <div className='rounded-md border p-3 space-y-3'>
              <p className='text-xs font-semibold uppercase text-muted-foreground'>
                NOA Fields
              </p>

              {/* Physician NPI */}
              <div className='space-y-1'>
                <label
                  htmlFor='noa-physician-npi'
                  className='text-sm font-medium'
                >
                  Physician NPI
                </label>
                <NpiInput
                  id='noa-physician-npi'
                  value={physicianNpi}
                  onValueChange={setPhysicianNpi}
                  placeholder='10-digit NPI'
                  inputSize='sm'
                  disabled={readOnly}
                />
              </div>

              {/* Physician name */}
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-1'>
                  <label
                    htmlFor='noa-physician-first-name'
                    className='text-sm font-medium'
                  >
                    Physician First Name
                  </label>
                  <Input
                    id='noa-physician-first-name'
                    value={physicianFirstName}
                    onChange={e => setPhysicianFirstName(e.target.value)}
                    inputSize='sm'
                    disabled={readOnly}
                  />
                </div>
                <div className='space-y-1'>
                  <label
                    htmlFor='noa-physician-last-name'
                    className='text-sm font-medium'
                  >
                    Physician Last Name
                  </label>
                  <Input
                    id='noa-physician-last-name'
                    value={physicianLastName}
                    onChange={e => setPhysicianLastName(e.target.value)}
                    inputSize='sm'
                    disabled={readOnly}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className='space-y-1'>
                <label className='text-sm font-medium'>Notes</label>
                <Textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder='Internal notes'
                  textareaSize='sm'
                  disabled={readOnly}
                />
              </div>

              {/* DCN / ICN Number */}
              <div className='space-y-1'>
                <label className='text-sm font-medium'>DCN / ICN Number</label>
                <Input
                  value={dcnIcnNumber}
                  onChange={e => setDcnIcnNumber(e.target.value)}
                  inputSize='sm'
                  disabled={readOnly}
                />
              </div>

              {/* Claim Number */}
              <div className='space-y-1'>
                <label className='text-sm font-medium'>Claim Number</label>
                <Input
                  value={claimNumber}
                  onChange={e => setClaimNumber(e.target.value)}
                  inputSize='sm'
                  disabled={readOnly}
                />
              </div>

              {/* Sent Date */}
              <DateField
                label='Sent Date'
                htmlFor='noa_sent_date'
                value={sentDate || null}
                onChange={v => setSentDate(v ?? '')}
                fieldSize='sm'
                disabled={readOnly}
              />
            </div>

            <div className='rounded-md border p-3 space-y-3'>
              <div>
                <p className='text-xs font-semibold uppercase text-muted-foreground'>
                  NOA Codes
                </p>
                <p className='text-xs text-muted-foreground'>
                  Codes entered here are saved on the NOA and copied to the
                  first episode's worksheet so they carry over to the claim.
                </p>
              </div>
              {!noa.primary_episode_id && (
                <p className='rounded-md bg-muted p-3 text-sm text-muted-foreground'>
                  This admission has no episode yet, so these codes save on the
                  NOA but won't reach a claim worksheet until one exists.
                </p>
              )}
              <div className='space-y-3'>
                <EditSection title='Codes'>
                  <div className='space-y-4'>
                    <div className='space-y-2'>
                      <p className='text-sm font-medium'>
                        FL 18-28 Condition codes
                      </p>
                      <ConditionCodesField
                        values={conditionCodes}
                        onChange={setConditionCodes}
                        readOnly={readOnly}
                      />
                    </div>
                    <div className='space-y-2'>
                      <p className='text-sm font-medium'>
                        FL 31-34 Occurrence codes
                      </p>
                      {/* 16 = two HI segments of 8 (TR3 max use 2), the 837I
                          ceiling; NOA readiness blocks a 17th server-side. */}
                      <OccurrenceCodesField
                        rows={occurrenceCodes}
                        onChange={setOccurrenceCodes}
                        maxRows={MAX_UB04_OCCURRENCE_CODES}
                        readOnly={readOnly}
                      />
                    </div>
                    <div className='space-y-2'>
                      <p className='text-sm font-medium'>
                        FL 35-36 Occurrence spans
                      </p>
                      <OccurrenceSpansField
                        rows={occurrenceSpans}
                        onChange={setOccurrenceSpans}
                        readOnly={readOnly}
                      />
                    </div>
                    <div className='space-y-2'>
                      <p className='text-sm font-medium'>
                        FL 39-41 Value codes
                      </p>
                      <ValueCodesField
                        rows={valueCodes}
                        onChange={setValueCodes}
                        readOnly={readOnly}
                        addRowActions={
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={handleGenerateValueCodes}
                            disabled={isBusy || readOnly}
                          >
                            {isGeneratingValueCodes
                              ? 'Generating…'
                              : 'Generate Location Codes (61 + 85)'}
                          </Button>
                        }
                      />
                    </div>
                  </div>
                </EditSection>
                <EditSection title='Remarks'>
                  <Textarea
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    placeholder='FL 80 remarks'
                    textareaSize='sm'
                    disabled={readOnly}
                    aria-invalid={remarksTooLong}
                    aria-describedby='noa-remarks-limit'
                  />
                  <p
                    id='noa-remarks-limit'
                    className={`text-xs text-right ${
                      remarksTooLong ? 'text-red-600' : 'text-muted-foreground'
                    }`}
                  >
                    {remarksLength}/{MAX_UB04_REMARKS_LENGTH}
                    {remarksTooLong
                      ? ` — FL 80 remarks supports ${MAX_UB04_REMARKS_LENGTH} characters.`
                      : null}
                  </p>
                </EditSection>
              </div>
            </div>

            {/* KX Modifier — only shown when NOA is late */}
            {noa.is_late && (
              <div className='rounded-md border border-orange-200 bg-orange-50 p-3 space-y-2'>
                <p className='text-sm font-medium text-orange-800 flex items-center gap-1'>
                  <AlertTriangle className='h-4 w-4' />
                  This NOA is being submitted late (
                  {Math.abs(noa.days_left ?? 0)} days overdue).
                </p>
                <p className='text-sm text-orange-700'>
                  Do you want to add a KX modifier on the final claim?
                </p>
                <div className='flex gap-4'>
                  <label className='flex items-center gap-2 text-sm cursor-pointer'>
                    <input
                      type='radio'
                      name='kx_modifier'
                      checked={kxModifier === true}
                      onChange={() => setKxModifier(true)}
                      disabled={readOnly}
                    />
                    Yes, add to claim
                  </label>
                  <label className='flex items-center gap-2 text-sm cursor-pointer'>
                    <input
                      type='radio'
                      name='kx_modifier'
                      checked={kxModifier === false}
                      onChange={() => setKxModifier(false)}
                      disabled={readOnly}
                    />
                    No, do not add
                  </label>
                </div>
                {kxModifier === true && (
                  <div className='space-y-1'>
                    <label className='text-sm font-medium text-orange-800'>
                      Add remarks for final claim
                    </label>
                    <Textarea
                      value={kxModifierRemarks}
                      onChange={e => setKxModifierRemarks(e.target.value)}
                      placeholder='Remarks for final claim…'
                      textareaSize='sm'
                      disabled={readOnly}
                      aria-invalid={kxModifierRemarksTooLong}
                      aria-describedby='noa-kx-remarks-limit'
                    />
                    <p
                      id='noa-kx-remarks-limit'
                      className={`text-xs text-right ${
                        kxModifierRemarksTooLong
                          ? 'text-red-600'
                          : 'text-orange-700'
                      }`}
                    >
                      {kxModifierRemarksLength}/{MAX_UB04_REMARKS_LENGTH}
                      {kxModifierRemarksTooLong
                        ? ` — NOA remarks supports ${MAX_UB04_REMARKS_LENGTH} characters.`
                        : null}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className='mt-3 rounded-md bg-red-50 border border-red-200 p-2'>
              <p className='text-sm text-red-700'>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className='mt-4 flex items-center flex-wrap gap-2 border-t pt-4'>
            <Button
              onClick={() => handleSave(true)}
              disabled={isBusy || readOnly}
              size='sm'
              className='grid place-items-center'
            >
              {/* Invisible copy of the widest label keeps the button from
                  shrinking when the text swaps to "Saving…" */}
              <span className='invisible col-start-1 row-start-1'>
                Save and Close
              </span>
              <span className='col-start-1 row-start-1'>
                {isSaving ? 'Saving…' : 'Save and Close'}
              </span>
            </Button>
            <Button
              onClick={() => handleSave(false)}
              disabled={isBusy || readOnly}
              size='sm'
              variant='outline'
            >
              Save
            </Button>
            <Button onClick={onClose} size='sm' variant='outline'>
              Cancel
            </Button>
            {/* Reading the history is never blocked by a read-only status. */}
            {canViewWorkLog && (
              <Button
                onClick={() => setWorkLogOpen(true)}
                size='sm'
                variant='outline'
              >
                <ClipboardList className='h-4 w-4 mr-1' />
                Work log
              </Button>
            )}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size='sm' variant='outline' disabled={isBusy}>
                  Actions
                  <ChevronDown className='h-3 w-3 ml-1' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='start'
                side='top'
                container={menuPortalContainer ?? undefined}
              >
                {/* A rejected NOA is corrected here and sent again in one
                    step: save, then submit (EMR-1471). */}
                <DropdownMenuItem
                  onClick={handleSaveAndSend}
                  disabled={
                    isBusy ||
                    (localStatus !== 'ready_to_send' &&
                      localStatus !== 'rejected') ||
                    blockedByErrors ||
                    !!noa.is_test_patient
                  }
                  title={
                    noa.is_test_patient
                      ? "This is a test patient — Roger won't submit it to Medicare."
                      : blockedByErrors
                        ? 'Fix the errors listed above before submitting.'
                        : undefined
                  }
                >
                  {localStatus === 'rejected'
                    ? 'Send Again'
                    : 'Submit to Medicare'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleForceMarkSent}
                  disabled={
                    isBusy || localStatus !== 'ready_to_send' || blockedByErrors
                  }
                  title={
                    blockedByErrors
                      ? 'Fix the errors listed above before marking this sent.'
                      : undefined
                  }
                >
                  Force Mark Sent
                </DropdownMenuItem>
                {canExport && <DropdownMenuSeparator />}
                {canExport && (
                  <DropdownMenuItem
                    onSelect={handleExportPdf}
                    disabled={isSaving || exportingId === noa.id}
                  >
                    <Download className='h-3 w-3 mr-2' />
                    {exportingId === noa.id
                      ? 'Opening UB-04 PDF…'
                      : 'Export UB-04 PDF'}
                  </DropdownMenuItem>
                )}
                {canExport && (
                  <>
                    <DropdownMenuItem onClick={() => handleExport('edi')}>
                      <Download className='h-3 w-3 mr-2' />
                      Export EDI
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => handleExport('json')}>
                        <Download className='h-3 w-3 mr-2' />
                        Export JSON
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {localStatus !== 'not_applicable' ? (
              <Button
                onClick={handleMarkNotApplicable}
                disabled={isBusy || readOnly}
                size='sm'
                variant='outline'
                className='ml-auto text-muted-foreground'
              >
                Mark Not Applicable
              </Button>
            ) : (
              <Button
                onClick={handleRevertToNotReady}
                disabled={isBusy}
                size='sm'
                variant='outline'
                className='ml-auto'
              >
                Revert to Not Yet Ready
              </Button>
            )}
          </div>
        </div>
        <EntityWorkLogDialog
          open={workLogOpen}
          onOpenChange={setWorkLogOpen}
          title='Work log'
          description='Edits to this Notice of Admission, oldest first.'
          fieldLabels={NOA_WORK_LOG_FIELD_LABELS}
          load={loadWorkLog}
        />

        <TestPatientSubmitConfirmDialog
          open={testPatientAckOpen}
          actionLabel='Notice of Admission'
          loading={isSaving}
          onConfirm={() => {
            setTestPatientAckOpen(false);
            void sendNoa(true);
          }}
          onCancel={() => setTestPatientAckOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

export default NOAForm;
